import * as React from 'react'
import {
  ADMINISTRATION_DEFAULT_ROUTE,
  TERRITORIAL_MANAGEMENT_DEFAULT_ROUTE,
} from '../../config/routes'
import * as RT from '../../config/routeType'
import { detectCityFromURL } from '../../utils/cityDetector'
import { buildCantonUrl } from '../../utils/cantonUrl'
import { useCities } from '../../context/CitiesContext'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch, useStore } from 'react-redux'
import { useTreePermissions } from '../../hooks/useTreePermissions'
import { TREE_PERMISSION, getTreeTenantRef } from '../../utils/treePermissions'
import { Tooltip, Drawer as MuiDrawer } from '@mui/material'
import { Stack, Divider, IconButton, List, Box } from '@mui/material'
import { ListItemButton, ListItemIcon, ListItemText } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import { show as showDrawerLeft } from '../../redux/slices/Drawer/drawerLeft'
import { show as showDrawerRight } from '../../redux/slices/Drawer/drawerRight'
import { setTreeCoords, setAddMode, setShowTenantAlert } from '../../redux/slices/Map/map'
import { setOpen } from '../../redux/slices/Modal/modalBoxConfirmation'
import ListMenu from './ListItem'
import TextDivider from './ListTextDivider'
//Icon
import ArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import ArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import ForestOutlinedIcon from '@mui/icons-material/ForestOutlined'
import logoapp from '../../assets/images/logo-app-blue.png'
import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined'
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import LocationCityIcon from '@mui/icons-material/LocationCity'

const closedMixin = theme => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowY: 'visible',
  width: `calc(${theme.spacing(6)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(5)} + 1px)`,
  },
  [theme.breakpoints.up('xs')]: {
    width: `calc(${theme.spacing(5)} + 1px)`,
  },
})

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}))

const widthIcon = { width: 20, height: 20 }

const DrawerLeftMenu = () => {
  const theme = useTheme()
  const refBox = React.useRef(null)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const { statusData, statusHeaders } = useSelector(state => state.report)
  const _stateDL = useSelector(state => state.drawerLeft)
  const _open = useSelector(state => state.drawerLeft.open)

  const authenticated = useSelector(state => state.login.authenticated)
  const { open: popupOpen, id } = useSelector(state => state.mapState.popup)
  // Se lee al hacer clic: suscribirse a las features re-renderizaría el menú con cada carga del mapa.
  const store = useStore()
  const isAdmin = authenticated?.user?.rol?.includes('administrador')

  const { canActInCanton, canRegisterAnyTenant, canUpdateAnyTenant, canUpdateTree, getPreferredCantonSlug } =
    useTreePermissions()
  const { getEnabledCantones } = useCities()
  const cantones = getEnabledCantones()

  const visible = _open ? 'block' : 'none'

  const widthMinDrawerLeft = !_open ? _stateDL.drawerWidthMin : _stateDL.drawerWidth

  const textBasic = {
    fontFamily: theme.typography.fontFamily,
    fontStyle: 'normal',
    fontSize: '0.85em',
  }

  const TextLabel = styled('div')(() => ({
    ...textBasic,
    lineHeight: '1.25em',
  }))

  const handleOpenCloseDrawerLeft = () => {
    // Quitar foco antes de abrir para evitar aria-hidden sobre elementos enfocados en #root
    if (!_open) document.activeElement?.blur()
    dispatch(showDrawerLeft(!_open))
  }
  // Determina el cantón destino: si no tiene el permiso en el actual, usa su cantón preferido para ese permiso
  const getTargetCity = permission => {
    const currentCity = detectCityFromURL(location.pathname) || RT.DEFAULT_CITY
    if (canActInCanton(permission, currentCity)) return currentCity
    return getPreferredCantonSlug(permission) || currentCity
  }

  const handleClickGeoref = () => {
    const targetCity = getTargetCity(TREE_PERMISSION.REGISTER)
    dispatch(showDrawerRight(false))
    dispatch(showDrawerLeft(false))
    dispatch(setTreeCoords(null))
    dispatch(setAddMode(false))
    navigate(buildCantonUrl(targetCity, cantones, `/${RT.REGISTER_TREE}`), { replace: false })
  }

  const handleClickManagement = () => {
    // Con popup abierto se edita ese árbol: exige permiso de actualizar en su tenant
    if (popupOpen && id) {
      const mapFeatures = store.getState().treeFeatures.data?.features || []
      const popupFeature = mapFeatures.find(feature => String(feature.properties?.id) === String(id))
      if (!canUpdateTree(getTreeTenantRef(popupFeature?.properties))) {
        dispatch(setShowTenantAlert(true))
        return
      }
    }
    const targetCity = getTargetCity(TREE_PERMISSION.UPDATE)
    dispatch(showDrawerRight(false))
    dispatch(showDrawerLeft(false))
    if (popupOpen && id) navigate(buildCantonUrl(targetCity, cantones, `/${RT.MANAGEMENT_TREE}/${id}`), { replace: false })
    else {
      dispatch(setTreeCoords(null))
      dispatch(setAddMode(false))
      navigate(buildCantonUrl(targetCity, cantones, `/${RT.MANAGEMENT_TREE}`), { replace: false })
    }
  }

  const handleGenerateReport = () => {
    dispatch(showDrawerRight(false))
    dispatch(showDrawerLeft(false))
    dispatch(setOpen(true))
  }

  const handleClickPanelSettings = () => {
    dispatch(showDrawerRight(false))
    dispatch(showDrawerLeft(false))
    dispatch(setOpen(false))
    navigate(ADMINISTRATION_DEFAULT_ROUTE, { replace: false })
  }

  const handleClickTerritorialManagement = () => {
    dispatch(showDrawerRight(false))
    dispatch(showDrawerLeft(false))
    dispatch(setOpen(false))
    navigate(TERRITORIAL_MANAGEMENT_DEFAULT_ROUTE, { replace: false })
  }

  const Drawer = styled(MuiDrawer, {
    shouldForwardProp: prop => true,
  })(({ theme, open }) => ({
    width: _stateDL.drawerWidth,
    height: '100vh',
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }))

  if (statusData === 'succeeded' && statusHeaders === 'succeeded') return null

  return (
    <Drawer
      variant="temporary"
      open={_open}
      PaperProps={{ sx: { position: 'initial' } }}
      onClose={(event, reason) =>
        reason === 'backdropClick' && handleOpenCloseDrawerLeft()
      }>
      <DrawerHeader
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: _stateDL.drawerHeight,
        }}>
        <Stack display={visible}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}>
            <img src={logoapp} alt="logo-app" width="100%" />
          </Box>
        </Stack>
        <IconButton
          onClick={handleOpenCloseDrawerLeft}
          sx={{
            display: visible,
            fontSize: 0,
            p: '7px',
            '&:hover': {
              color: theme.palette.text.white,
              bgcolor: theme.palette.primary.light,
            },
          }}>
          <ArrowLeftIcon sx={{ width: 20, height: 20 }} />
        </IconButton>
      </DrawerHeader>
      <Divider sx={{ borderTop: `2px solid ${theme.palette.primary.main}` }} />

      <Box
        ref={refBox}
        sx={{
          width: widthMinDrawerLeft,
          color: theme.palette.primary.main,
          overflowY: 'auto',
        }}
        role="presentation">
        {(canRegisterAnyTenant || canUpdateAnyTenant) && (
          <>
            <TextDivider
              display={!_open}
              label="Administración Forestal"
              stylelabel={{
                pl: '14px',
                fontWeight: 600,
                color: theme.palette.primary.main,
              }}
              sx={{ borderTop: `2px solid ${theme.palette.primary.main}` }}
            />
            <List>
              {canUpdateAnyTenant && (
                <ListMenu
                  simple={true}
                  keyText="editarArbol"
                  onClick={handleClickManagement}
                  primary="Editar árbol"
                  iconType={<ForestOutlinedIcon sx={widthIcon} />}
                />
              )}
              {canRegisterAnyTenant && (
                <ListMenu
                  simple={true}
                  keyText="georef"
                  onClick={handleClickGeoref}
                  primary="Registrar árbol"
                  iconType={<AddLocationAltOutlinedIcon sx={widthIcon} />}
                />
              )}
            </List>
          </>
        )}

        {isAdmin && (
          <>
            <TextDivider
              display={!_open}
              label="Panel de configuración"
              stylelabel={{
                pl: '14px',
                fontWeight: 600,
                color: theme.palette.primary.main,
              }}
              sx={{ borderTop: `2px solid ${theme.palette.primary.main}` }}
            />
            <List>
              {isAdmin && (
                <ListMenu
                  simple={true}
                  keyText="panelsettings"
                  onClick={handleClickPanelSettings}
                  primary="Administrar DB"
                  iconType={<AdminPanelSettingsIcon sx={widthIcon} />}
                />
              )}
              <ListMenu
                simple={true}
                keyText="inventario"
                onClick={handleGenerateReport}
                primary="Inventario"
                iconType={<SummarizeOutlinedIcon sx={widthIcon} />}
              />
              <ListMenu
                simple={true}
                keyText="territorialManagement"
                onClick={handleClickTerritorialManagement}
                primary="Administración Territorial"
                iconType={<LocationCityIcon sx={widthIcon} />}
              />
            </List>
          </>
        )}
      </Box>
      <Divider />
      <Box display="flex" flexGrow="1" width={widthMinDrawerLeft}>
        <List sx={{ height: '100%', width: '100%' }}>
          <ListMenu simple={false} keyText="bIconFooter" sx={{ height: '100%' }}>
            <Tooltip
              title={!_open ? 'Expandir' : ''}
              placement="left"
              sx={{ height: '100%' }}>
              <ListItemButton
                onClick={handleOpenCloseDrawerLeft}
                sx={{ px: !_open ? 0.5 : 2, height: '100%', py: 0 }}>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: _open ? 1 : 'auto',
                    justifyContent: 'center',
                  }}>
                  <IconButton>
                    {!_open ? (
                      <ArrowRightIcon sx={widthIcon} />
                    ) : (
                      <ArrowLeftIcon sx={widthIcon} />
                    )}
                  </IconButton>
                </ListItemIcon>
                <ListItemText sx={{ display: visible }}>
                  <TextLabel sx={{ fontWeight: 400, lineHeight: '1em' }}>
                    Colapsar
                  </TextLabel>
                </ListItemText>
              </ListItemButton>
            </Tooltip>
          </ListMenu>
        </List>
      </Box>
    </Drawer>
  )
}
export default DrawerLeftMenu
